import { buildDefaultMatrixConfig } from '@/components/views/Matrix/classes/IMatrixConfig';
import type IMatrixConfig from '@/components/views/Matrix/classes/IMatrixConfig';
import type { CategoryId, GroupId, RaceId, StationRoleId } from '@/utilities/typealiases';
import type { MatrixSettings } from './MatrixSettings';
import type { RacesGroupsCategoriesMap } from '@/utilities/RacesGroupsCategoriesMap';
import type { StationRole } from '@/objects/generated';

type StationRoles = { id: StationRoleId; enabled: boolean }[];
type RaceGroupIDs = { id: GroupId; enabled: boolean }[];
type CategoryIDs = { id: CategoryId; enabled: boolean }[];

type StationRolesMap = Map<RaceId, StationRoles>;
type RaceGroupMap = Map<RaceId, RaceGroupIDs>;
type CategoryMap = Map<RaceId, CategoryIDs>;

/**
 * Version 2 of matrix settings.
 * This saves ALL stationroles/racegroups/categories, so we know if something has been added after this was saved to db
 */
export class SavedMatrixSettingsV2 {
    static NAME_OF_CLASS = 'SavedMatrixSettingsV2';
    public MatrixConfig: IMatrixConfig;

    public StationRolesMap: StationRolesMap;
    public RaceGroupMap: RaceGroupMap;
    public CategoryMap: CategoryMap;

    public Version = 2;

    /**
     * Builds a SavedMatrixSettingsV2 object from a MatrixSettings object. This is used when we want to save the settings to the db.
     * @param matrixSettings The current MatrixSettings
     * @param racesGroupsCategoriesMap The raceGroupCategories map for the event as it currently is
     * @param stationRoles All the stationRoles for the event
     * @returns A SavedMatrixSettingsV2 object
     */
    public static FromMatrixSettings(
        matrixSettings: MatrixSettings,
        racesGroupsCategoriesMap: RacesGroupsCategoriesMap,
        stationRoles: StationRole[],
    ): SavedMatrixSettingsV2 {
        const savedSettings = new SavedMatrixSettingsV2();

        savedSettings.MatrixConfig = matrixSettings.MatrixConfig;

        savedSettings.StationRolesMap = new Map<RaceId, StationRoles>();
        savedSettings.RaceGroupMap = new Map<RaceId, RaceGroupIDs>();
        savedSettings.CategoryMap = new Map<RaceId, CategoryIDs>();

        // Initialize with all the ids
        racesGroupsCategoriesMap.GetRaceIDs().forEach((raceId) => {
            savedSettings.CategoryMap.set(
                raceId,
                racesGroupsCategoriesMap.GetCategoryIDs([], raceId).map((id) => ({ id, enabled: false })),
            );

            savedSettings.RaceGroupMap.set(
                raceId,
                racesGroupsCategoriesMap.GetGroupIDs(raceId).map((id) => ({ id, enabled: false })),
            );
            savedSettings.StationRolesMap.set(
                raceId,
                stationRoles.filter((sr) => sr.RaceID === raceId || sr.RaceID === null).map((sr) => ({ id: sr.ID, enabled: false })),
            );
        });

        // Enable only the ones that are enabled in the matrix settings
        matrixSettings.CategoryMap.forEach((categories, raceId) => {
            const savedCategories = savedSettings.CategoryMap.get(raceId);
            if (savedCategories) {
                categories.forEach((categoryId) => {
                    const category = savedCategories.find((c) => c.id === categoryId);
                    if (category) {
                        category.enabled = true;
                    }
                });
            }
        });

        matrixSettings.RaceGroupMap.forEach((groups, raceId) => {
            const savedGroups = savedSettings.RaceGroupMap.get(raceId);
            if (savedGroups) {
                groups.forEach((groupId) => {
                    const group = savedGroups.find((g) => g.id === groupId);
                    if (group) {
                        group.enabled = true;
                    }
                });
            }
        });

        matrixSettings.StationRolesMap.forEach((stationRoles, raceId) => {
            const savedStationRoles = savedSettings.StationRolesMap.get(raceId);
            if (savedStationRoles) {
                stationRoles.forEach((stationRoleId) => {
                    const stationRole = savedStationRoles.find((sr) => sr.id === stationRoleId);
                    if (stationRole) {
                        stationRole.enabled = true;
                    }
                });
            }
        });

        return savedSettings;
    }

    public constructor(val?: SavedMatrixSettingsV2) {
        this.StationRolesMap = val?.StationRolesMap ?? new Map<RaceId, StationRoles>();
        this.RaceGroupMap = val?.RaceGroupMap ?? new Map<RaceId, RaceGroupIDs>();
        this.CategoryMap = val?.CategoryMap ?? new Map<RaceId, CategoryIDs>();

        this.MatrixConfig = buildDefaultMatrixConfig(val?.MatrixConfig);

        // Reset the RaceID so we dont have a preselected race
        this.MatrixConfig.RaceID = null;

        // Reset search so we dont have a saved search
        this.MatrixConfig.SearchString = '';
    }

    public serialize(): string {
        return JSON.stringify(this, (key, value) => {
            // Maps cant be serialized to JSON so we need to convert them to arrays
            // An array with this format can be parsed back into a map
            if (['StationRolesMap', 'RaceGroupMap', 'CategoryMap'].includes(key)) {
                return Array.from(value.entries());
            }

            return value;
        });
    }

    public deserialize(json: any): any {
        return JSON.parse(json, (key, value) => {
            // If the key is one of the maps we need to convert the array back to a map
            if (['StationRolesMap', 'RaceGroupMap', 'CategoryMap'].includes(key)) {
                return new Map(value);
            }

            return value;
        });
    }
}
